import _ from 'underscore';

import {FolderSummary} from 'constants/FolderConstants';
import {FinderResult, PROJECTS_FINDER_ID, REGISTRIES_FINDER_ID} from 'constants/NavConstants';
import FinderUtil from 'file-browser/FinderUtil';
import FolderDAO from 'file-browser/FolderDAO';
import * as FileUtil from 'folder-items/FileUtil';
import {ListableItem} from 'search/SearchConstants';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';

export function shouldDetectShortcuts(finderId: string): boolean {
  return finderId === PROJECTS_FINDER_ID;
}

export type ShouldShowShortcutItem = FileUtil.IsInRegistryItem;

export function shouldShowShortcut(file: ShouldShowShortcutItem, finderId?: string): boolean {
  if (!APIUtil.isBiologicalEntity(file.id)) {
    return false;
  }
  if (finderId === REGISTRIES_FINDER_ID) {
    // Never want to display shortcuts in registry listing
    return false;
  } else if (FileUtil.isInRegistry(file)) {
    // This enables the project shortcut icon to show up for all registered items in views outside
    // of registry listing
    return true;
  }
  return false;
}

export type GetParentFolderIdsWithShortcutsItem = ShouldShowShortcutItem &
  Pick<FinderResult, 'id' | 'folder' | 'parent'> & {projectFolderId: string};

export function getParentFolderIdsWithShortcuts(
  files: Array<GetParentFolderIdsWithShortcutsItem>,
  finderId: string
): Array<string> {
  return files
    .map((file) => {
      if (shouldShowShortcut(file, finderId)) {
        return file.projectFolderId;
      }
      return FinderUtil.parentFolderId(file);
    })
    .filter((file) => file != null);
}

export async function projectFolder(projectFolderId: string): Promise<FolderSummary | null> {
  try {
    return (await FolderDAO.fetch(projectFolderId, {summary: 1})) ?? null;
  } catch (e) {
    if (net.isServerRequestError(e) && e.status === 404) {
      return null;
    }
    throw e;
  }
}

/**
 * Given a list of registered listables and a list of nonregistered listables, find their common parent
 * folder, if any.
 */
export function findCommonProjectFolderId(
  registryItems: Array<ListableItem>,
  nonRegistryItems: Array<ListableItem>
): string | null {
  const projectFolderIds = _.unique(
    registryItems.map((file) => {
      // `ListableItem` includes `AaSequenceAlignmentSummaryGraphQL`. AA Sequence Alignments cannot be
      // registered, but there are no good type discriminators for `ListableItem`, so we just check for
      // presence of the `projectFolderId` for typesafety.
      if ('projectFolderId' in file && file.projectFolderId) {
        return file.projectFolderId;
      }
      return null;
    })
  );
  const folderIds = _.unique(
    nonRegistryItems.map((file) => {
      // Same issue as above - `AaSequenceAlignmentSummaryGraphQL` does not have `folder` because it is always
      // attached to an AA Sequence, and always moves with that sequence.
      if ('folder' in file && file.folder) {
        // `ListableItem` includes both items with `StringIdFolder` and items with `NumberIdFolder`. We always
        // want the stringy id, so check for presence of `api_identifier` before assuming that `id` is what
        // we want.
        if ('api_identifier' in file.folder) {
          return file.folder.api_identifier;
        } else {
          return file.folder.id;
        }
      }
      return null;
    })
  );
  const allIds = _.unique([...projectFolderIds, ...folderIds]);
  if (allIds.length > 1) {
    return null;
  }
  return allIds[0];
}
